/**
 * SchoolModule - Main School Institute System module
 * Integrated into the existing admin event page as sidebar sections
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  GraduationCap, Users, UserCog, BookOpen, Calendar, ClipboardCheck,
  Plus, Search, Check, X, ChevronRight, Loader2, School, Shield, Settings, Eye,
  BarChart3, AlertTriangle, BookMarked, Bus, CreditCard, Trophy, Video, Bell, Clock,
  FileText, Banknote, Activity, TrendingUp, History, Upload, Briefcase, Heart, MapPin, Phone,
  MoreHorizontal, KeyRound, CheckCircle, Trash2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { StudentTimeline } from '@/components/school/SchoolLifecycle';
import { StudentBulkImport } from '@/components/school/StudentBulkImport';
import { GraduationCheckDialog } from '@/components/school/GraduationProgress';
import { getToken } from '@/lib/authToken';
import { useCampusContext } from '@/components/school/CampusContext';
import { useAcademicContext } from '@/components/school/AcademicContext';
import ModuleDirectoryCard from '@/components/school/ModuleDirectoryCard';
import CampusesCard from '@/components/school/CampusesCard';
import SchoolSlugCard from '@/components/school/SchoolSlugCard';
import SchoolShareCard from '@/components/school/SchoolShareCard';
import CampusHealthLightsCard from '@/components/school/CampusHealthLightsCard';
import ReadinessHub from '@/components/workflow/ReadinessHub';
import AdmissionFormBuilder from '@/components/school/AdmissionFormBuilder';
import SchoolSetupChecklist from '@/components/school/SchoolSetupChecklist';
import QuickEnrolWizard from '@/components/school/QuickEnrolWizard';
import { ListState, ListStateRow } from '@/components/school/ListState';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.detail || 'Request failed');
    // iter 639 — the status was thrown away here, so every list rendered
    // "No records found" whether the request was refused, the session had
    // expired or the server had fallen over. The caller needs to tell those
    // apart to say anything useful.
    error.status = res.status;
    throw error;
  }
  return res.json();
};

const uploadPhoto = async (tenantId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/upload-photo`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${getToken()}` },
    body: formData,
  });
  if (!res.ok) throw new Error('Upload failed');
  return res.json();
};

const PhotoUpload = ({ url, onUpload, tenantId }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadPhoto(tenantId, file);
      onUpload(result.url);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden shrink-0">
        {url ? <img src={`${API_URL}${url}`} alt="" className="w-full h-full object-cover" /> : <Users className="h-6 w-6 text-slate-300" />}
      </div>
      <div>
        <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-sm rounded-md transition-colors">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? 'Uploading...' : 'Upload Photo'}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
        <p className="text-[10px] text-slate-400 mt-0.5">JPEG, PNG, WebP. Max 5MB</p>
      </div>
    </div>
  );
};


// ============== SCHOOL DASHBOARD ==============
export const SchoolDashboard = ({ tenantId, onNavigate }) => {
  const [showQuickEnrol, setShowQuickEnrol] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['school-dashboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/dashboard`),
    enabled: !!tenantId,
  });

  // Map checklist action_path values to existing SchoolDetailPage tab keys.
  const goTo = (path) => {
    // Special case: the 'Enrol Students' step opens the QuickEnrolWizard
    // in-place rather than navigating to the students tab (which has no
    // dedicated bulk-enrol UI).
    if (path === 'students-enrol') { setShowQuickEnrol(true); return; }
    if (!onNavigate) return;
    const map = {
      'settings/academic': 'academic-years',
      'classes': 'classes',
      'subjects': 'subjects',
      'team': 'team',
      'students': 'students',
      'timetable': 'timetable',
      'fees': 'fees',
    };
    onNavigate(map[path] || path);
  };

  if (isLoading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  if (!data) return null;

  const stats = data.stats || {};
  const att = data.attendance_today || {};
  const lib = data.library || {};
  const fees = data.fees || {};
  const transport = data.transport || {};
  const access = data.access || {};
  const exams = data.exams || {};
  const lms = data.lms || {};

  return (
    <div className="space-y-6" data-testid="school-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{data.school?.name || 'School'}</h2>
          <p className="text-sm text-slate-500">{data.academic_year ? `Academic Year: ${data.academic_year.name}` : 'No academic year set'}</p>
        </div>
        <Badge variant="outline" className="text-indigo-600 border-indigo-200">{data.school?.code}</Badge>
      </div>

      {/* Module Directory — at-a-glance composable modules */}
      <ModuleDirectoryCard tenantId={tenantId} />

      {/* Setup Checklist — show admins what they need to fill before portals come alive */}
      <SchoolSetupChecklist tenantId={tenantId} onNavigate={goTo} />

      {/* Campus Pulse — red/amber/green per-campus health */}
      <CampusHealthLightsCard tenantId={tenantId} />

      {/* Campuses — multi-campus support with broadcast */}
      <CampusesCard tenantId={tenantId} />

      {/* Public URL slug (super admin only) */}
      <SchoolSlugCard tenantId={tenantId} />

      {/* Share & Admissions QR — printable poster + downloadable PNG */}
      <SchoolShareCard tenantId={tenantId} />

      {/* Core Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Students', value: stats.total_students, icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Teachers', value: stats.total_teachers, icon: UserCog, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Classes', value: stats.total_classes, icon: School, color: 'text-purple-600 bg-purple-50' },
          { label: 'Subjects', value: stats.total_subjects, icon: BookOpen, color: 'text-amber-600 bg-amber-50' },
          { label: 'Pending Admissions', value: stats.pending_admissions, icon: ClipboardCheck, color: 'text-rose-600 bg-rose-50' },
          { label: 'Guardians', value: stats.total_guardians, icon: Shield, color: 'text-teal-600 bg-teal-50' },
        ].map(c => (
          <Card key={c.label} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${c.color}`}>
                <c.icon className="h-4 w-4" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{c.value ?? 0}</p>
              <p className="text-xs text-slate-500">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Module Widgets */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Attendance Today */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-attendance">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-green-600" /> Attendance Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#22c55e" strokeWidth="3"
                    strokeDasharray={`${att.rate || 0} ${100 - (att.rate || 0)}`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{att.rate || 0}%</span>
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Present</span><span className="font-medium text-green-600">{att.present || 0}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Absent</span><span className="font-medium text-red-600">{att.absent || 0}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Recorded</span><span className="font-medium">{att.total_recorded || 0}</span></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Exams & Grading */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-exams">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-600" /> Exams & Grading</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-xl font-bold text-amber-700">{exams.active || 0}</p>
                <p className="text-xs text-amber-600">Active Exams</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xl font-bold text-green-700">{exams.published || 0}</p>
                <p className="text-xs text-green-600">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* LMS */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-lms">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><GraduationCap className="h-4 w-4 text-indigo-600" /> LMS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-indigo-50 rounded-lg text-center">
                <p className="text-xl font-bold text-indigo-700">{lms.published_lessons || 0}</p>
                <p className="text-xs text-indigo-600">Lessons</p>
              </div>
              <div className="p-3 bg-rose-50 rounded-lg text-center">
                <p className="text-xl font-bold text-rose-700">{lms.upcoming_live || 0}</p>
                <p className="text-xs text-rose-600">Upcoming Live</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Library */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-library">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BookMarked className="h-4 w-4 text-sky-600" /> Library</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-1">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Total Books</span><span className="font-medium">{lib.total_books || 0}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Borrowed</span><span className="font-medium text-blue-600">{lib.borrowed || 0}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Overdue</span><span className="font-medium text-red-600">{lib.overdue || 0}</span></div>
              </div>
              {lib.overdue > 0 && <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" />{lib.overdue} overdue</Badge>}
            </div>
          </CardContent>
        </Card>

        {/* Finance */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-fees">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Banknote className="h-4 w-4 text-emerald-600" /> Fees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Collected</span>
                <span className="text-sm font-bold text-emerald-600">${(fees.collected || 0).toLocaleString()}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${fees.collected && fees.pending ? Math.min(100, (fees.collected / (fees.collected + fees.pending)) * 100) : 0}%` }} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Pending</span>
                <span className="text-sm font-medium text-amber-600">${(fees.pending || 0).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transport & Access */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-transport-access">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Bus className="h-4 w-4 text-violet-600" /> Transport & Access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="p-2 bg-violet-50 rounded-lg">
                <p className="text-lg font-bold text-violet-700">{transport.active_routes || 0}</p>
                <p className="text-[10px] text-violet-600">Routes</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <p className="text-lg font-bold text-blue-700">{transport.students_assigned || 0}</p>
                <p className="text-[10px] text-blue-600">On Bus</p>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <p className="text-lg font-bold text-green-700">{access.entries_today || 0}</p>
                <p className="text-[10px] text-green-600">Entries Today</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <p className="text-lg font-bold text-amber-700">{access.active_passes || 0}</p>
                <p className="text-[10px] text-amber-600">Active Passes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Alerts + Announcements + Activity */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* At-Risk Alert */}
        <Card className={`border-0 shadow-sm ${data.at_risk_count > 0 ? 'ring-1 ring-red-200' : ''}`} data-testid="dashboard-at-risk">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className={`h-4 w-4 ${data.at_risk_count > 0 ? 'text-red-600' : 'text-slate-400'}`} /> At-Risk Students</CardTitle>
          </CardHeader>
          <CardContent>
            {data.at_risk_count > 0 ? (
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-700">{data.at_risk_count}</p>
                <p className="text-xs text-red-600">students flagged as at-risk</p>
              </div>
            ) : (
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <Check className="h-6 w-6 text-green-600 mx-auto mb-1" />
                <p className="text-xs text-green-600">No at-risk students</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Announcements */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-announcements">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Bell className="h-4 w-4 text-amber-600" /> Recent Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.recent_announcements || []).length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3">No recent announcements</p>
            ) : (
              <div className="space-y-2">
                {data.recent_announcements.slice(0, 4).map(ann => (
                  <div key={ann.id} className="flex items-center gap-2 py-1">
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${ann.priority === 'urgent' ? 'bg-red-500' : ann.priority === 'high' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                    <span className="text-xs text-slate-700 truncate flex-1">{ann.title}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-0 shadow-sm" data-testid="dashboard-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-indigo-600" /> Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.recent_activity || []).length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3">No recent activity</p>
            ) : (
              <div className="space-y-2">
                {data.recent_activity.slice(0, 5).map((act, i) => (
                  <div key={i} className="flex items-start gap-2 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700 truncate">{typeof act.details === 'string' ? act.details : (act.action || '')}</p>
                      <p className="text-[10px] text-slate-400">{act.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <QuickEnrolWizard
        open={showQuickEnrol}
        onClose={() => setShowQuickEnrol(false)}
        tenantId={tenantId}
      />
      {/* Aggregated workflow readiness across all setup-driven modules. */}
      <ReadinessHub tenantId={tenantId} onNavigate={onNavigate} />
    </div>
  );
};


// ============== ADMISSIONS ==============
export const SchoolAdmissions = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', date_of_birth: '', gender: '', previous_school: '', applying_for_class: '', guardian_name: '', guardian_phone: '', guardian_email: '', guardian_relationship: 'parent', notes: '' });
  const [selectedClassId, setSelectedClassId] = useState('');
  const { campusId, campuses } = useCampusContext(tenantId);
  const currentCampus = campuses.find(c => c.id === campusId);

  const { data: admissionsRaw = [], isLoading, error, refetch } = useQuery({
    queryKey: ['school-admissions', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/admissions`),
    enabled: !!tenantId,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
    enabled: !!tenantId,
  });

  // Filter admissions by the chosen campus (admissions inherit the campus_id of
  // their applying_for_class). Empty campus = show everything.
  const admissions = React.useMemo(() => {
    if (!campusId) return admissionsRaw;
    const classCampus = new Map(classes.map(c => [c.id, c.campus_id || '']));
    return (admissionsRaw || []).filter(a => {
      if (a.campus_id && a.campus_id === campusId) return true;
      if (a.class_id && classCampus.get(a.class_id) === campusId) return true;
      // Show shared (no campus) admissions too so nothing disappears
      const cc = a.campus_id || classCampus.get(a.class_id) || '';
      return !cc;
    });
  }, [admissionsRaw, classes, campusId]);

  const submitMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/admissions`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`Application ${d.application_number} submitted`); setShowForm(false); setForm({ first_name: '', last_name: '', date_of_birth: '', gender: '', previous_school: '', applying_for_class: '', guardian_name: '', guardian_phone: '', guardian_email: '', guardian_relationship: 'parent', notes: '' }); queryClient.invalidateQueries({ queryKey: ['school-admissions'] }); },
    onError: (e) => toast.error(e.message),
  });

  const approveMutation = useMutation({
    mutationFn: ({ admissionId, classId }) => api(`/tenants/${tenantId}/admissions/${admissionId}/approve?class_id=${classId}`, { method: 'POST' }),
    onSuccess: (d) => { toast.success(d.message); queryClient.invalidateQueries({ queryKey: ['school-admissions'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); queryClient.invalidateQueries({ queryKey: ['school-students'] }); },
    onError: (e) => toast.error(e.message),
  });

  const rejectMutation = useMutation({
    mutationFn: (admissionId) => api(`/tenants/${tenantId}/admissions/${admissionId}/reject`, { method: 'POST' }),
    onSuccess: () => { toast.success('Application rejected'); queryClient.invalidateQueries({ queryKey: ['school-admissions'] }); },
    onError: (e) => toast.error(e.message),
  });

  // Edit admission
  const [editingAdmission, setEditingAdmission] = useState(null);
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api(`/tenants/${tenantId}/admissions/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => { toast.success('Application updated'); setEditingAdmission(null); queryClient.invalidateQueries({ queryKey: ['school-admissions'] }); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/admissions/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Application deleted'); queryClient.invalidateQueries({ queryKey: ['school-admissions'] }); },
    onError: (e) => toast.error(e.message),
  });

  const statusColor = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-4" data-testid="school-admissions">
      <Tabs defaultValue="applications">
        <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
          <TabsTrigger value="applications" data-testid="admissions-tab-applications">Applications</TabsTrigger>
          <TabsTrigger value="form-builder" data-testid="admissions-tab-form-builder">Form Builder</TabsTrigger>
        </TabsList>
        <TabsContent value="applications" className="space-y-4 mt-4">
      {campusId && currentCampus && (
        <div className="text-xs text-slate-500" data-testid="admissions-campus-scope">
          Showing admissions for <b>{currentCampus.name}</b>{` (${admissions.length} of ${(admissionsRaw || []).length})`}. Switch the campus chip at the top to change.
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Admissions</h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${tenantId}/admissions/export.csv`;
              const token = getToken();
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((blob) => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `admissions-${tenantId}.csv`;
                  a.click();
                });
            }}
            data-testid="admissions-export-csv"
          >
            <FileText className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-admission-btn"><Plus className="h-4 w-4 mr-1" /> New Application</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Admission Application</DialogTitle>
              <DialogDescription>Submit a student admission application</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name" /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" /></div>
              <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Previous School</Label><Input value={form.previous_school} onChange={e => setForm({ ...form, previous_school: e.target.value })} placeholder="Previous school name" /></div>
              <div className="col-span-2"><Label>Applying For Class</Label><Input value={form.applying_for_class} onChange={e => setForm({ ...form, applying_for_class: e.target.value })} placeholder="e.g. Grade 1" /></div>
              <div className="col-span-2 border-t pt-3 mt-1"><p className="font-medium text-sm text-slate-700 mb-2">Guardian Information</p></div>
              <div><Label>Guardian Name *</Label><Input value={form.guardian_name} onChange={e => setForm({ ...form, guardian_name: e.target.value })} placeholder="Full name" /></div>
              <div><Label>Guardian Phone *</Label><Input value={form.guardian_phone} onChange={e => setForm({ ...form, guardian_phone: e.target.value })} placeholder="+263..." /></div>
              <div><Label>Guardian Email</Label><Input type="email" value={form.guardian_email} onChange={e => setForm({ ...form, guardian_email: e.target.value })} placeholder="email@example.com" /></div>
              <div>
                <Label>Relationship</Label>
                <Select value={form.guardian_relationship} onValueChange={v => setForm({ ...form, guardian_relationship: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="parent">Parent</SelectItem><SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sponsor">Sponsor</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes" /></div>
            </div>
            <DialogFooter className="mt-4">
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={() => submitMutation.mutate(form)} disabled={submitMutation.isPending || !form.first_name || !form.last_name || !form.guardian_name || !form.guardian_phone}>
                {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Submit Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>App #</TableHead>
              <TableHead>Student Name</TableHead>
              <TableHead>Guardian</TableHead>
              <TableHead>Applying For</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admissions.length === 0 ? (
              <ListStateRow colSpan={6} noun="applications" testId="admissions-state"
                            isLoading={isLoading} error={error} count={admissions.length}
                            filtersActive={!!campusId && (admissionsRaw || []).length > 0}
                            filterSummary={currentCampus ? [`campus ${currentCampus.name}`] : []}
                            onRetry={refetch} />
            ) : admissions.map(a => (
              <TableRow key={a.id} data-testid={`admission-row-${a.id}`}>
                <TableCell className="font-mono text-xs">{a.application_number}</TableCell>
                <TableCell className="font-medium">
                  {a.first_name} {a.last_name}
                  {a.custom_data && Object.keys(a.custom_data).length > 0 && (
                    <Badge variant="outline" className="ml-2 text-[10px] text-indigo-700 border-indigo-200 bg-indigo-50" data-testid={`admission-custom-badge-${a.id}`}>
                      +{Object.keys(a.custom_data).length} custom
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600">{a.guardian_name}<br /><span className="text-xs text-slate-400">{a.guardian_phone}</span></TableCell>
                <TableCell>{a.applying_for_class}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[a.status] || ''}`}>{a.status}</span></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 flex-wrap">
                    {a.status === 'pending' && (
                      <>
                        <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                          <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Assign class" /></SelectTrigger>
                          <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="h-8 text-green-600" data-testid={`approve-${a.id}`}
                          onClick={() => approveMutation.mutate({ admissionId: a.id, classId: selectedClassId })}
                          disabled={!selectedClassId || approveMutation.isPending}>
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-red-600" data-testid={`reject-${a.id}`}
                          onClick={() => rejectMutation.mutate(a.id)} disabled={rejectMutation.isPending}>
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                    {a.status === 'approved' && <span className="text-xs text-slate-500">Adm#: {a.admission_number}</span>}
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Edit application"
                      data-testid={`admission-edit-${a.id}`}
                      onClick={() => setEditingAdmission({ ...a })}>
                      <Settings className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                      title={a.status === 'approved' ? 'Approved admissions cannot be deleted' : 'Delete application'}
                      data-testid={`admission-delete-${a.id}`}
                      disabled={a.status === 'approved'}
                      onClick={() => {
                        if (window.confirm(`Delete application ${a.application_number}? This cannot be undone.`)) {
                          deleteMutation.mutate(a.id);
                        }
                      }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Admission Dialog */}
      <Dialog open={!!editingAdmission} onOpenChange={(o) => !o && setEditingAdmission(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="admission-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>{editingAdmission?.application_number}</DialogDescription>
          </DialogHeader>
          {editingAdmission && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><Label>First Name</Label><Input value={editingAdmission.first_name || ''} onChange={e => setEditingAdmission({ ...editingAdmission, first_name: e.target.value })} /></div>
              <div><Label>Last Name</Label><Input value={editingAdmission.last_name || ''} onChange={e => setEditingAdmission({ ...editingAdmission, last_name: e.target.value })} /></div>
              <div><Label>Date of Birth</Label><Input type="date" value={editingAdmission.date_of_birth || ''} onChange={e => setEditingAdmission({ ...editingAdmission, date_of_birth: e.target.value })} /></div>
              <div>
                <Label>Gender</Label>
                <Select value={editingAdmission.gender || ''} onValueChange={v => setEditingAdmission({ ...editingAdmission, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Previous School</Label><Input value={editingAdmission.previous_school || ''} onChange={e => setEditingAdmission({ ...editingAdmission, previous_school: e.target.value })} /></div>
              <div className="col-span-2"><Label>Applying For Class</Label><Input value={editingAdmission.applying_for_class || ''} onChange={e => setEditingAdmission({ ...editingAdmission, applying_for_class: e.target.value })} /></div>
              <div><Label>Guardian Name</Label><Input value={editingAdmission.guardian_name || ''} onChange={e => setEditingAdmission({ ...editingAdmission, guardian_name: e.target.value })} /></div>
              <div><Label>Guardian Phone</Label><Input value={editingAdmission.guardian_phone || ''} onChange={e => setEditingAdmission({ ...editingAdmission, guardian_phone: e.target.value })} /></div>
              <div><Label>Guardian Email</Label><Input type="email" value={editingAdmission.guardian_email || ''} onChange={e => setEditingAdmission({ ...editingAdmission, guardian_email: e.target.value })} /></div>
              <div>
                <Label>Relationship</Label>
                <Select value={editingAdmission.guardian_relationship || 'parent'} onValueChange={v => setEditingAdmission({ ...editingAdmission, guardian_relationship: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="parent">Parent</SelectItem><SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sponsor">Sponsor</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Input value={editingAdmission.notes || ''} onChange={e => setEditingAdmission({ ...editingAdmission, notes: e.target.value })} /></div>
              {editingAdmission.custom_data && Object.keys(editingAdmission.custom_data).length > 0 && (
                <div className="col-span-2 border-t pt-3 mt-1" data-testid="admission-custom-data-section">
                  <p className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-500" /> Custom answers
                    <span className="text-xs text-slate-400 font-normal">(from the school's custom form)</span>
                  </p>
                  <div className="space-y-2">
                    {Object.entries(editingAdmission.custom_data).map(([key, value]) => (
                      <div key={key} className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm py-1 border-b border-slate-100 last:border-0" data-testid={`admission-custom-${key}`}>
                        <div className="text-slate-500 font-medium capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="col-span-2 text-slate-800 break-words">
                          {value === '' || value === null || value === undefined
                            ? <span className="text-slate-300">—</span>
                            : Array.isArray(value)
                              ? value.join(', ')
                              : typeof value === 'boolean'
                                ? (value ? 'Yes' : 'No')
                                : String(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditingAdmission(null)}>Cancel</Button>
            <Button
              data-testid="admission-edit-save"
              disabled={updateMutation.isPending}
              onClick={() => {
                const { id, application_number, status, created_at, updated_at, created_by, tenant_id, _id, source, ...payload } = editingAdmission;
                updateMutation.mutate({ id: editingAdmission.id, payload });
              }}
            >
              {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="form-builder" className="mt-4">
          <AdmissionFormBuilder tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};


// ============== STUDENTS ==============
export const SchoolStudents = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState('list');
  const [search, setSearch] = useState('');
  const [filterHouse, setFilterHouse] = useState('');
  const [filterDisability, setFilterDisability] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showHouseForm, setShowHouseForm] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [houseForm, setHouseForm] = useState({ name: '', code: '', color: '#6366f1', motto: '', description: '' });
  const [studentProfile, setStudentProfile] = useState(null);
  const [graduationFor, setGraduationFor] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const emptyForm = { first_name: '', last_name: '', email: '', date_of_birth: '', gender: '', admission_number: '', class_id: '', status: 'active', photo_url: '', blood_group: '', nationality: '', religion: '', address: '', previous_school: '', hobbies: '', special_needs: '', immunization_status: '', medical_conditions: '', allergies: '', birth_certificate_number: '', emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '', house_id: '', disability_type: 'none', iep_status: 'none', accessibility_needs: [], counselor_notes: '', campus_id: '' };
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['school-students', tenantId, search, filterHouse, filterDisability, filterCampus, filterGender, filterClass, filterStatus],
    queryFn: () => {
      let url = `/tenants/${tenantId}/students?search=${search}&status=${filterStatus || 'active'}`;
      if (filterHouse) url += `&house_id=${filterHouse}`;
      if (filterDisability && filterDisability !== 'none') url += `&disability_type=${filterDisability}`;
      if (filterCampus) url += `&campus_id=${filterCampus}`;
      if (filterGender) url += `&gender=${filterGender}`;
      if (filterClass) url += `&class_id=${filterClass}`;
      return api(url);
    },
    enabled: !!tenantId,
  });

  // iter 639 — how many there are before the other filters narrow it, so a
  // filtered zero reads as "0 of 66" rather than as an empty register.
  const { data: unfiltered } = useQuery({
    queryKey: ['school-students-unfiltered', tenantId, filterStatus],
    queryFn: () => api(`/tenants/${tenantId}/students?status=${filterStatus || 'active'}&limit=1`),
    enabled: !!tenantId,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
    enabled: !!tenantId,
  });

  const { data: houses = [] } = useQuery({
    queryKey: ['school-houses', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/houses`),
    enabled: !!tenantId,
  });

  const { data: campusesData } = useQuery({
    queryKey: ['school-campuses', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/campuses`),
    enabled: !!tenantId,
  });
  const campuses = campusesData?.items || [];

  const { data: houseDashboard } = useQuery({
    queryKey: ['school-houses-dashboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/houses/dashboard`),
    enabled: !!tenantId && activeSubTab === 'houses',
  });

  const createMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/students`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Student added'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-students'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/students/${d.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Student updated'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-students'] }); },
    onError: (e) => toast.error(e.message),
  });

  const closeForm = () => { setShowForm(false); setEditingStudent(null); setForm(emptyForm); };

  const createHouseMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/houses`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('House created'); setShowHouseForm(false); setEditingHouse(null); setHouseForm({ name: '', code: '', color: '#6366f1', motto: '', description: '' }); queryClient.invalidateQueries({ queryKey: ['school-houses'] }); queryClient.invalidateQueries({ queryKey: ['school-houses-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateHouseMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/houses/${d.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('House updated'); setShowHouseForm(false); setEditingHouse(null); setHouseForm({ name: '', code: '', color: '#6366f1', motto: '', description: '' }); queryClient.invalidateQueries({ queryKey: ['school-houses'] }); queryClient.invalidateQueries({ queryKey: ['school-houses-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteHouseMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/houses/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('House removed'); queryClient.invalidateQueries({ queryKey: ['school-houses'] }); queryClient.invalidateQueries({ queryKey: ['school-houses-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const loadProfile = async (studentId) => {
    setLoadingProfile(true);
    try {
      const profile = await api(`/tenants/${tenantId}/students/${studentId}/profile`);
      setStudentProfile(profile);
    } catch (e) { toast.error('Failed to load profile'); }
    setLoadingProfile(false);
  };

  const resetPasswordMutation = useMutation({
    mutationFn: (studentId) => api(`/tenants/${tenantId}/admin/reset-user-password`, { method: 'POST', body: JSON.stringify({ user_type: 'student', user_id: studentId, send_email: true }) }),
    onSuccess: (data) => { setResetResult(data); toast.success(`Password reset! ${data.email_sent ? 'Email sent.' : 'Copy the temp password.'}`); },
    onError: (e) => toast.error(e.message),
  });

  const openEdit = (s) => {
    setEditingStudent(s);
    setForm({
      first_name: s.first_name, last_name: s.last_name, email: s.email || '', date_of_birth: s.date_of_birth || '',
      gender: s.gender || '', admission_number: s.admission_number || '', class_id: s.current_class?.id || '', status: s.status || 'active',
      photo_url: s.photo_url || '', blood_group: s.blood_group || '', nationality: s.nationality || '', religion: s.religion || '',
      address: s.address || '', previous_school: s.previous_school || '', hobbies: s.hobbies || '', special_needs: s.special_needs || '',
      immunization_status: s.immunization_status || '', medical_conditions: s.medical_conditions || '', allergies: s.allergies || '',
      birth_certificate_number: s.birth_certificate_number || '',
      emergency_contact_name: s.emergency_contact_name || '', emergency_contact_phone: s.emergency_contact_phone || '',
      emergency_contact_relationship: s.emergency_contact_relationship || '',
      house_id: s.house_id || s.house?.id || '', disability_type: s.disability_type || 'none',
      iep_status: s.iep_status || 'none', accessibility_needs: s.accessibility_needs || [],
      counselor_notes: s.counselor_notes || '',
      campus_id: s.campus_id || '',
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (editingStudent) updateMutation.mutate({ ...form, id: editingStudent.id });
    else createMutation.mutate(form);
  };

  const students = data?.students || [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  // iter 639 — what is actually narrowing the list, in the office's own words.
  // The live report of "0 students" turned out to be a campus selection with
  // no active students behind it; the page gave no hint that a filter was on.
  const filterSummary = [
    search && `search “${search}”`,
    filterHouse && `house ${houses.find(h => h.id === filterHouse)?.name || ''}`.trim(),
    filterClass && `class ${classes.find(c => c.id === filterClass)?.name || ''}`.trim(),
    filterGender && `gender ${filterGender}`,
    filterCampus && (filterCampus === 'shared'
      ? 'shared (tenant-wide)'
      : `campus ${campuses.find(c => c.id === filterCampus)?.name || ''}`.trim()),
    filterDisability && filterDisability !== 'none' && `need ${filterDisability}`,
    filterStatus !== 'active' && `status ${filterStatus}`,
  ].filter(Boolean);
  // "Active" is a default the office never chose, so an empty active list is
  // still a filtered result, not an empty register.
  const filtersActive = filterSummary.length > 0 || filterStatus === 'active';
  // Anything beyond the status default is actively narrowing the list.
  const narrowed = filterSummary.some((f) => !f.startsWith('status '));
  const clearFilters = () => {
    setSearch(''); setFilterHouse(''); setFilterClass(''); setFilterGender('');
    setFilterCampus(''); setFilterDisability(''); setFilterStatus('all');
  };

  return (
    <div className="space-y-4" data-testid="school-students">
      {/* Sub-tab Navigation */}
      <div className="flex items-center gap-4 border-b pb-2">
        <button onClick={() => setActiveSubTab('list')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeSubTab === 'list' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="students-list-tab">Students</button>
        <button onClick={() => setActiveSubTab('houses')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeSubTab === 'houses' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="houses-tab">Houses</button>
      </div>

      {activeSubTab === 'list' && (
        <>
          {graduationFor && (
            <GraduationCheckDialog tenantId={tenantId} student={graduationFor}
                                   onClose={() => setGraduationFor(null)} />
          )}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-slate-900" data-testid="students-heading">
              Students{' '}
              {error
                /* A failed request used to read "Students (0)", which is a
                   claim about the register rather than about the request. */
                ? <span className="text-sm font-normal text-amber-600">(count unavailable)</span>
                : `(${data?.total ?? 0}${
                    narrowed && unfiltered?.total > (data?.total ?? 0)
                      ? ` of ${unfiltered.total}` : ''
                  }${filterStatus !== 'all' ? ` ${filterStatus}` : ''})`}
            </h2>
            <div className="flex gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input className="pl-9" placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} data-testid="student-search-input" />
              </div>
              <Select value={filterHouse || "all"} onValueChange={v => setFilterHouse(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36" data-testid="filter-house"><SelectValue placeholder="House" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Houses</SelectItem>
                  {houses.map(h => <SelectItem key={h.id} value={h.id}><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: h.color }} />{h.name}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterClass || "all"} onValueChange={v => setFilterClass(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40" data-testid="filter-class"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterGender || "all"} onValueChange={v => setFilterGender(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-32" data-testid="filter-gender"><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="graduated">Graduated</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              {(filterHouse || filterClass || filterGender || filterCampus || filterDisability || filterStatus !== 'active' || search) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-500 hover:text-rose-600"
                  onClick={() => { setSearch(''); setFilterHouse(''); setFilterClass(''); setFilterGender(''); setFilterCampus(''); setFilterDisability(''); setFilterStatus('active'); }}
                  data-testid="filter-reset-btn"
                >
                  <X className="h-4 w-4 mr-1" /> Reset
                </Button>
              )}
              {campuses.length > 0 && (
                <Select value={filterCampus || "all"} onValueChange={v => setFilterCampus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40" data-testid="filter-campus"><SelectValue placeholder="Campus" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campuses</SelectItem>
                    <SelectItem value="shared">Shared (tenant-wide)</SelectItem>
                    {campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" onClick={() => { setEditingStudent(null); setForm(emptyForm); setShowForm(true); }} data-testid="add-student-btn"><Plus className="h-4 w-4 mr-1" /> Add Student</Button>
              <Button size="sm" variant="outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-btn"><Upload className="h-4 w-4 mr-1" /> Import</Button>
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Adm #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>House</TableHead>
                  <TableHead>Guardian</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.length === 0 ? (
                  <ListStateRow
                    colSpan={8} noun="students" testId="students-state"
                    isLoading={isLoading} error={error} count={students.length}
                    filtersActive={filtersActive} filterSummary={filterSummary}
                    onClear={clearFilters} onRetry={refetch}
                  />
                ) : students.map(s => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                        {s.photo_url ? <img src={`${API_URL}${s.photo_url}`} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-medium text-slate-400">{(s.first_name?.[0] || '')}{(s.last_name?.[0] || '')}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.admission_number}</TableCell>
                    <TableCell className="font-medium">
                      {s.first_name} {s.last_name}
                      {s.campus_id && (
                        <Badge variant="outline" className="ml-2 text-[10px]" data-testid={`student-campus-${s.id}`}>
                          {campuses.find(c => c.id === s.campus_id)?.name || 'Campus'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{s.current_class?.name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                    <TableCell>
                      {s.house ? (
                        <Badge variant="outline" className="text-xs" style={{ borderColor: s.house.color, color: s.house.color }}>{s.house.name}</Badge>
                      ) : <span className="text-slate-400 text-xs">-</span>}
                    </TableCell>
                    <TableCell>
                      {s.guardians?.length > 0 ? (
                        <span className="text-sm">{s.guardians[0].first_name} {s.guardians[0].last_name}</span>
                      ) : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.status === 'active' ? 'default' : 'secondary'} className={s.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}>
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setViewStudent(s); loadProfile(s.id); }} data-testid={`view-student-${s.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`}><Settings className="h-4 w-4" /></Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setGraduationFor(s)} data-testid={`graduation-check-${s.id}`}>
                            <GraduationCap className="h-4 w-4 mr-2" /> Graduation check
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { if (window.confirm(`Reset password for ${s.first_name} ${s.last_name}?`)) resetPasswordMutation.mutate(s.id); }}>
                            <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* ============ HOUSES SUB-TAB ============ */}
      {activeSubTab === 'houses' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Student Houses</h2>
            <Button size="sm" onClick={() => { setEditingHouse(null); setHouseForm({ name: '', code: '', color: '#6366f1', motto: '', description: '' }); setShowHouseForm(true); }} data-testid="add-house-btn"><Plus className="h-4 w-4 mr-1" /> Add House</Button>
          </div>

          {houseDashboard && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
              <Card className="p-3 text-center bg-slate-50">
                <p className="text-2xl font-bold text-slate-800">{houseDashboard.houses?.length || 0}</p>
                <p className="text-xs text-slate-500">Total Houses</p>
              </Card>
              <Card className="p-3 text-center bg-amber-50">
                <p className="text-2xl font-bold text-amber-700">{houseDashboard.unassigned_students || 0}</p>
                <p className="text-xs text-amber-600">Unassigned Students</p>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {houses.map(h => (
              <Card key={h.id} className="overflow-hidden" data-testid={`house-card-${h.id}`}>
                <div className="h-1.5" style={{ background: h.color }} />
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold" style={{ color: h.color }}>{h.name}</h3>
                      <p className="text-xs text-slate-500 font-mono">{h.code}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingHouse(h); setHouseForm({ name: h.name, code: h.code, color: h.color || '#6366f1', motto: h.motto || '', description: h.description || '' }); setShowHouseForm(true); }}><Settings className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => { if (window.confirm(`Delete ${h.name}? Students must be reassigned first.`)) deleteHouseMutation.mutate(h.id); }}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  {h.motto && <p className="text-xs text-slate-600 italic">"{h.motto}"</p>}
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{h.student_count}</span>
                    <span className="text-slate-500">students</span>
                  </div>
                  {h.house_master_name && <p className="text-xs text-slate-500">House Master: <span className="font-medium">{h.house_master_name}</span></p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {houses.length === 0 && <div className="text-center py-10 text-slate-400"><Trophy className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>No houses created yet</p></div>}
        </div>
      )}

      {/* ============ STUDENT FORM DIALOG ============ */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="student-form-desc">
          <DialogHeader><DialogTitle>{editingStudent ? 'Edit Student' : 'Add Student'}</DialogTitle><p id="student-form-desc" className="sr-only">Student form</p></DialogHeader>
          <Tabs defaultValue="basic" className="mt-2">
            <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 w-full md:inline-flex md:grid-cols-none md:w-auto">
              <TabsTrigger value="basic" className="text-xs">Basic Info</TabsTrigger>
              <TabsTrigger value="medical" className="text-xs">Medical</TabsTrigger>
              <TabsTrigger value="accessibility" className="text-xs">Accessibility</TabsTrigger>
              <TabsTrigger value="emergency" className="text-xs">Emergency</TabsTrigger>
              <TabsTrigger value="additional" className="text-xs">Additional</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-3 mt-3">
              <PhotoUpload url={form.photo_url} onUpload={url => setForm({ ...form, photo_url: url })} tenantId={tenantId} />
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="student-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="student-last-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Admission Number</Label><Input value={form.admission_number} onChange={e => setForm({ ...form, admission_number: e.target.value })} placeholder="Auto-generated if empty" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></div>
                <div><Label>Gender</Label>
                  <Select value={form.gender || "unset"} onValueChange={v => setForm({ ...form, gender: v === "unset" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="unset">-</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Class</Label>
                  <Select value={form.class_id || "unassigned"} onValueChange={v => setForm({ ...form, class_id: v === "unassigned" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {campuses.length > 0 && (
                  <div><Label>Campus</Label>
                    <Select value={form.campus_id || "shared"} onValueChange={v => setForm({ ...form, campus_id: v === "shared" ? "" : v })}>
                      <SelectTrigger data-testid="student-campus-select"><SelectValue placeholder="Select campus" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shared">Tenant-wide (shared)</SelectItem>
                        {campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>House</Label>
                  <Select value={form.house_id || "unassigned"} onValueChange={v => setForm({ ...form, house_id: v === "unassigned" ? "" : v })}>
                    <SelectTrigger data-testid="student-house-select"><SelectValue placeholder="Select house" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">No House</SelectItem>
                      {houses.map(h => <SelectItem key={h.id} value={h.id}><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: h.color }} />{h.name}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem><SelectItem value="graduated">Graduated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Nationality</Label><Input value={form.nationality} onChange={e => setForm({ ...form, nationality: e.target.value })} /></div>
                <div><Label>Religion</Label><Input value={form.religion} onChange={e => setForm({ ...form, religion: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={2} /></div>
            </TabsContent>

            <TabsContent value="medical" className="space-y-3 mt-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
                <Shield className="h-4 w-4 shrink-0" /> Sensitive data is encrypted at rest for security
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Blood Group</Label>
                  <Select value={form.blood_group || "unset"} onValueChange={v => setForm({ ...form, blood_group: v === "unset" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">-</SelectItem>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Immunization Status</Label>
                  <Select value={form.immunization_status || "unset"} onValueChange={v => setForm({ ...form, immunization_status: v === "unset" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">-</SelectItem><SelectItem value="up_to_date">Up to Date</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem><SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Medical Conditions</Label><Textarea value={form.medical_conditions} onChange={e => setForm({ ...form, medical_conditions: e.target.value })} placeholder="Any known medical conditions..." rows={2} /></div>
              <div><Label>Allergies</Label><Textarea value={form.allergies} onChange={e => setForm({ ...form, allergies: e.target.value })} placeholder="Food, medication, or other allergies..." rows={2} /></div>
              <div><Label>Special Needs / Accommodations</Label><Textarea value={form.special_needs} onChange={e => setForm({ ...form, special_needs: e.target.value })} rows={2} /></div>
              <div><Label>Birth Certificate Number</Label><Input value={form.birth_certificate_number} onChange={e => setForm({ ...form, birth_certificate_number: e.target.value })} /></div>
            </TabsContent>

            <TabsContent value="accessibility" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Disability Type</Label>
                  <Select value={form.disability_type || 'none'} onValueChange={v => setForm({ ...form, disability_type: v })}>
                    <SelectTrigger data-testid="disability-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="physical">Physical</SelectItem>
                      <SelectItem value="visual">Visual</SelectItem>
                      <SelectItem value="hearing">Hearing</SelectItem>
                      <SelectItem value="cognitive">Cognitive</SelectItem>
                      <SelectItem value="learning">Learning</SelectItem>
                      <SelectItem value="speech">Speech</SelectItem>
                      <SelectItem value="multiple">Multiple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>IEP Status</Label>
                  <Select value={form.iep_status || 'none'} onValueChange={v => setForm({ ...form, iep_status: v })}>
                    <SelectTrigger data-testid="iep-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="review">Under Review</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Accessibility Needs</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {['wheelchair', 'braille', 'sign_language', 'large_print', 'hearing_aid', 'modified_desk', 'extra_time', 'assistive_tech'].map(need => (
                    <label key={need} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.accessibility_needs?.includes(need)} onChange={e => {
                        const needs = form.accessibility_needs || [];
                        setForm({ ...form, accessibility_needs: e.target.checked ? [...needs, need] : needs.filter(n => n !== need) });
                      }} className="rounded border-slate-300" />
                      <span className="capitalize">{need.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div><Label>Counselor Notes</Label><Textarea value={form.counselor_notes} onChange={e => setForm({ ...form, counselor_notes: e.target.value })} rows={3} placeholder="Confidential counselor notes (encrypted)" /></div>
            </TabsContent>

            <TabsContent value="emergency" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Emergency Contact Name *</Label><Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                <div><Label>Phone *</Label><Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
                <div><Label>Relationship</Label>
                  <Select value={form.emergency_contact_relationship || "unset"} onValueChange={v => setForm({ ...form, emergency_contact_relationship: v === "unset" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">-</SelectItem><SelectItem value="father">Father</SelectItem><SelectItem value="mother">Mother</SelectItem>
                      <SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sibling">Sibling</SelectItem><SelectItem value="uncle">Uncle</SelectItem>
                      <SelectItem value="aunt">Aunt</SelectItem><SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="additional" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Previous School</Label><Input value={form.previous_school} onChange={e => setForm({ ...form, previous_school: e.target.value })} /></div>
                <div className="col-span-2"><Label>Hobbies / Extracurriculars</Label><Textarea value={form.hobbies} onChange={e => setForm({ ...form, hobbies: e.target.value })} rows={2} /></div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isPending || !form.first_name || !form.last_name} data-testid="student-submit-btn">
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editingStudent ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Student Detail Dialog - Enhanced with Profile Data */}
      <Dialog open={!!viewStudent} onOpenChange={() => { setViewStudent(null); setStudentProfile(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="student-detail-desc">
          <DialogHeader><DialogTitle>Student Profile</DialogTitle><p id="student-detail-desc" className="sr-only">View student information</p></DialogHeader>
          {viewStudent && (
            <Tabs defaultValue="info">
              <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
                <TabsTrigger value="info">Profile</TabsTrigger>
                <TabsTrigger value="academics">Academics</TabsTrigger>
                <TabsTrigger value="medical">Medical</TabsTrigger>
                <TabsTrigger value="accessibility">Accessibility</TabsTrigger>
                <TabsTrigger value="emergency">Emergency</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>
              <TabsContent value="info" className="mt-3">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-20 h-20 rounded-full bg-slate-100 border flex items-center justify-center overflow-hidden shrink-0">
                    {viewStudent.photo_url ? <img src={`${API_URL}${viewStudent.photo_url}`} alt="" className="w-full h-full object-cover" /> : <Users className="h-8 w-8 text-slate-300" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{viewStudent.first_name} {viewStudent.last_name}</h3>
                    <p className="text-sm text-slate-500 font-mono">{viewStudent.admission_number}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge variant={viewStudent.status === 'active' ? 'default' : 'secondary'} className={viewStudent.status === 'active' ? 'bg-green-100 text-green-700' : ''}>{viewStudent.status}</Badge>
                      {viewStudent.house && <Badge variant="outline" style={{ borderColor: viewStudent.house.color, color: viewStudent.house.color }}>{viewStudent.house.name || viewStudent.house?.name}</Badge>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Email:</span><p>{viewStudent.email || '-'}</p></div>
                  <div><span className="text-slate-500">Gender:</span><p className="capitalize">{viewStudent.gender || '-'}</p></div>
                  <div><span className="text-slate-500">Date of Birth:</span><p>{viewStudent.date_of_birth || '-'}</p></div>
                  <div><span className="text-slate-500">Class:</span><p>{viewStudent.current_class?.name || 'Unassigned'}</p></div>
                  <div><span className="text-slate-500">Nationality:</span><p>{viewStudent.nationality || '-'}</p></div>
                  <div><span className="text-slate-500">Religion:</span><p>{viewStudent.religion || '-'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Address:</span><p>{viewStudent.address || '-'}</p></div>
                </div>
                {viewStudent.guardians?.length > 0 && (
                  <div className="border-t pt-3 mt-3">
                    <p className="font-medium mb-2 text-sm">Guardians</p>
                    {viewStudent.guardians.map((g, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Users className="h-3.5 w-3.5 text-slate-400" />
                        <span>{g.first_name || g.name} {g.last_name || ''} ({g.relationship}) {g.phone ? `- ${g.phone}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="academics" className="mt-3 space-y-4">
                {loadingProfile ? (
                  <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
                ) : studentProfile ? (
                  <>
                    {/* Fee Summary */}
                    <div>
                      <p className="font-medium text-sm mb-2 flex items-center gap-1"><Banknote className="h-4 w-4 text-green-600" /> Fee Summary</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <Card className="p-2.5 text-center bg-green-50"><p className="text-lg font-bold text-green-700">${studentProfile.fee_summary?.total_payments?.toFixed(2) || '0.00'}</p><p className="text-[10px] text-green-600">Paid</p></Card>
                        <Card className="p-2.5 text-center bg-red-50"><p className="text-lg font-bold text-red-700">${studentProfile.fee_summary?.balance?.toFixed(2) || '0.00'}</p><p className="text-[10px] text-red-600">Balance</p></Card>
                        <Card className="p-2.5 text-center bg-slate-50"><p className="text-lg font-bold text-slate-700">${studentProfile.fee_summary?.total_charges?.toFixed(2) || '0.00'}</p><p className="text-[10px] text-slate-600">Total Charges</p></Card>
                      </div>
                    </div>
                    {/* Attendance Summary */}
                    <div>
                      <p className="font-medium text-sm mb-2 flex items-center gap-1"><ClipboardCheck className="h-4 w-4 text-blue-600" /> Attendance (Last 30 Days)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Card className="p-2 text-center"><p className="text-lg font-bold">{studentProfile.attendance_summary?.rate || 0}%</p><p className="text-[10px] text-slate-500">Rate</p></Card>
                        <Card className="p-2 text-center bg-green-50"><p className="text-lg font-bold text-green-700">{studentProfile.attendance_summary?.present || 0}</p><p className="text-[10px] text-green-600">Present</p></Card>
                        <Card className="p-2 text-center bg-red-50"><p className="text-lg font-bold text-red-700">{studentProfile.attendance_summary?.absent || 0}</p><p className="text-[10px] text-red-600">Absent</p></Card>
                        <Card className="p-2 text-center bg-amber-50"><p className="text-lg font-bold text-amber-700">{studentProfile.attendance_summary?.late || 0}</p><p className="text-[10px] text-amber-600">Late</p></Card>
                      </div>
                    </div>
                    {/* Class History */}
                    {studentProfile.class_history?.length > 0 && (
                      <div>
                        <p className="font-medium text-sm mb-2 flex items-center gap-1"><History className="h-4 w-4 text-indigo-600" /> Class History</p>
                        <div className="space-y-1">
                          {studentProfile.class_history.map((ch, i) => (
                            <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-1.5">
                              <span className="font-medium">{ch.class_name}</span>
                              <Badge variant="outline" className="text-xs">{ch.status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">No academic data available</p>
                )}
              </TabsContent>

              <TabsContent value="medical" className="mt-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 shrink-0" /> This data is encrypted at rest
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Blood Group:</span><p className="font-medium">{viewStudent.blood_group || '-'}</p></div>
                  <div><span className="text-slate-500">Immunization:</span><p className="capitalize">{viewStudent.immunization_status?.replace(/_/g, ' ') || '-'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Medical Conditions:</span><p>{viewStudent.medical_conditions || 'None reported'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Allergies:</span><p>{viewStudent.allergies || 'None reported'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Special Needs:</span><p>{viewStudent.special_needs || 'None'}</p></div>
                  <div><span className="text-slate-500">Birth Certificate #:</span><p className="font-mono text-xs">{viewStudent.birth_certificate_number || '-'}</p></div>
                </div>
              </TabsContent>

              <TabsContent value="accessibility" className="mt-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Disability Type:</span><p className="capitalize font-medium">{(viewStudent.disability_type || 'none').replace(/_/g, ' ')}</p></div>
                  <div><span className="text-slate-500">IEP Status:</span><p className="capitalize">{(viewStudent.iep_status || 'none').replace(/_/g, ' ')}</p></div>
                </div>
                {viewStudent.accessibility_needs?.length > 0 && (
                  <div className="mt-3">
                    <span className="text-slate-500 text-sm">Accessibility Needs:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {viewStudent.accessibility_needs.map(n => (
                        <Badge key={n} variant="outline" className="text-xs capitalize">{n.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {viewStudent.counselor_notes && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-3">
                    <span className="text-slate-500 text-sm">Counselor Notes:</span>
                    <p className="text-sm mt-1">{viewStudent.counselor_notes}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="emergency" className="mt-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Contact Name:</span><p className="font-medium">{viewStudent.emergency_contact_name || '-'}</p></div>
                  <div><span className="text-slate-500">Phone:</span><p>{viewStudent.emergency_contact_phone || '-'}</p></div>
                  <div><span className="text-slate-500">Relationship:</span><p className="capitalize">{viewStudent.emergency_contact_relationship || '-'}</p></div>
                </div>
              </TabsContent>
              <TabsContent value="timeline" className="mt-3">
                <StudentTimeline tenantId={tenantId} studentId={viewStudent.id} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="bulk-import-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-indigo-600" /> Bulk Import Students</DialogTitle>
            <p id="bulk-import-desc" className="text-xs text-slate-500">Upload a CSV or Excel file to import students from another system</p>
          </DialogHeader>
          <StudentBulkImport tenantId={tenantId} onComplete={() => { queryClient.invalidateQueries({ queryKey: ['school-students'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); }} />
        </DialogContent>
      </Dialog>

      {/* House Form Dialog */}
      <Dialog open={showHouseForm} onOpenChange={(open) => { if (!open) { setShowHouseForm(false); setEditingHouse(null); } }}>
        <DialogContent aria-describedby="house-form-desc">
          <DialogHeader><DialogTitle>{editingHouse ? 'Edit House' : 'Add House'}</DialogTitle><p id="house-form-desc" className="sr-only">House form</p></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={houseForm.name} onChange={e => setHouseForm({ ...houseForm, name: e.target.value })} placeholder="Eagle House" data-testid="house-name-input" /></div>
              <div><Label>Code *</Label><Input value={houseForm.code} onChange={e => setHouseForm({ ...houseForm, code: e.target.value.toUpperCase() })} placeholder="EAGLE" data-testid="house-code-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Color</Label><Input type="color" value={houseForm.color} onChange={e => setHouseForm({ ...houseForm, color: e.target.value })} className="h-10" /></div>
              <div><Label>Motto</Label><Input value={houseForm.motto} onChange={e => setHouseForm({ ...houseForm, motto: e.target.value })} placeholder="Soar High" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={houseForm.description} onChange={e => setHouseForm({ ...houseForm, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowHouseForm(false); setEditingHouse(null); }}>Cancel</Button>
            <Button onClick={() => { if (editingHouse) updateHouseMutation.mutate({ ...houseForm, id: editingHouse.id }); else createHouseMutation.mutate(houseForm); }} disabled={!houseForm.name || !houseForm.code} data-testid="house-submit-btn">
              {editingHouse ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Result Dialog */}
      <Dialog open={!!resetResult} onOpenChange={(open) => !open && setResetResult(null)}>
        <DialogContent aria-describedby="reset-result-desc">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-600" /> Password Reset</DialogTitle><p id="reset-result-desc" className="sr-only">Reset result</p></DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <p className="text-sm">Password for <strong>{resetResult.user_type}</strong> has been reset.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-blue-600 mb-1">Temporary Password</p>
                <p className="text-2xl font-mono font-bold text-blue-800 tracking-wider" data-testid="temp-password">{resetResult.temp_password}</p>
              </div>
              {resetResult.email_sent ? (
                <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Email sent to {resetResult.email}</p>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Email not sent. Please share the password manually.</p>
              )}
              <Button className="w-full" onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success('Copied!'); }}>Copy Password</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============== TEACHERS ==============
export const SchoolTeachers = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [viewTeacher, setViewTeacher] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const emptyForm = { first_name: '', last_name: '', email: '', phone: '', department: '', employee_id: '', subjects: [], photo_url: '', bio: '', specialization: '', years_of_experience: 0, employment_type: 'full-time', date_joined: '', secondary_email: '', languages: '', teaching_license: '', qualifications: [], national_id: '', emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '', campus_id: '' };
  const [form, setForm] = useState(emptyForm);
  const [newQual, setNewQual] = useState({ degree: '', institution: '', year: '', field_of_study: '' });

  const { data: teachers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['school-teachers', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/teachers`),
    enabled: !!tenantId,
  });

  const { data: tCampusesData } = useQuery({
    queryKey: ['school-campuses', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/campuses`),
    enabled: !!tenantId,
  });
  const tCampuses = tCampusesData?.items || [];

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/teachers`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Teacher added'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-teachers'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/teachers/${d.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Teacher updated'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-teachers'] }); },
    onError: (e) => toast.error(e.message),
  });

  const [resetTeacherResult, setResetTeacherResult] = useState(null);

  const resetTeacherPasswordMutation = useMutation({
    mutationFn: (teacherId) => api(`/tenants/${tenantId}/admin/reset-user-password`, { method: 'POST', body: JSON.stringify({ user_type: 'teacher', user_id: teacherId, send_email: true }) }),
    onSuccess: (data) => { setResetTeacherResult(data); toast.success(`Password reset! ${data.email_sent ? 'Email sent.' : 'Copy the temp password.'}`); },
    onError: (e) => toast.error(e.message),
  });

  const closeForm = () => { setShowForm(false); setEditingTeacher(null); setForm(emptyForm); setNewQual({ degree: '', institution: '', year: '', field_of_study: '' }); };

  const openEdit = (t) => {
    setEditingTeacher(t);
    setForm({
      first_name: t.first_name, last_name: t.last_name, email: t.email, phone: t.phone || '', department: t.department || '',
      employee_id: t.employee_id || '', subjects: t.subjects || [],
      photo_url: t.photo_url || '', bio: t.bio || '', specialization: t.specialization || '',
      years_of_experience: t.years_of_experience || 0, employment_type: t.employment_type || 'full-time',
      date_joined: t.date_joined || '', secondary_email: t.secondary_email || '',
      languages: t.languages || '', teaching_license: t.teaching_license || '',
      qualifications: t.qualifications || [], national_id: t.national_id || '',
      emergency_contact_name: t.emergency_contact_name || '',
      emergency_contact_phone: t.emergency_contact_phone || '',
      emergency_contact_relationship: t.emergency_contact_relationship || '',
      campus_id: t.campus_id || '',
    });
    setShowForm(true);
  };

  const addQualification = () => {
    if (!newQual.degree) return;
    setForm({ ...form, qualifications: [...form.qualifications, { ...newQual }] });
    setNewQual({ degree: '', institution: '', year: '', field_of_study: '' });
  };

  const removeQualification = (idx) => {
    setForm({ ...form, qualifications: form.qualifications.filter((_, i) => i !== idx) });
  };

  const handleSubmit = () => {
    if (editingTeacher) updateMutation.mutate({ ...form, id: editingTeacher.id });
    else createMutation.mutate(form);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Qualification verification
  const { data: qualifications = [] } = useQuery({
    queryKey: ['qual-verification', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/qualifications/pending`),
    enabled: !!tenantId,
  });

  const verifyMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/qualifications/verify`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Qualification updated'); queryClient.invalidateQueries({ queryKey: ['qual-verification'] }); queryClient.invalidateQueries({ queryKey: ['school-teachers'] }); },
    onError: (e) => toast.error(e.message),
  });

  const pendingCount = qualifications.filter(q => (q.verification_status || 'pending') === 'pending').length;

  return (
    <div className="space-y-4" data-testid="school-teachers">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Teachers ({teachers.length})</h2>
        <div className="flex gap-2">
          <Button size="sm" variant={showVerification ? "default" : "outline"} onClick={() => setShowVerification(!showVerification)} data-testid="verification-toggle-btn">
            <ClipboardCheck className="h-4 w-4 mr-1" /> Verify Qualifications
            {pendingCount > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px]">{pendingCount}</Badge>}
          </Button>
          <Button size="sm" onClick={() => { setEditingTeacher(null); setForm(emptyForm); setShowForm(true); }} data-testid="add-teacher-btn"><Plus className="h-4 w-4 mr-1" /> Add Teacher</Button>
        </div>
      </div>

      {/* Qualification Verification Panel */}
      {showVerification && (
        <Card data-testid="verification-panel">
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-amber-600" /> Qualifications Verification</h3>
            {qualifications.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No qualifications to review</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Teacher</TableHead><TableHead>Qualification</TableHead><TableHead>Institution</TableHead><TableHead>Year</TableHead><TableHead>Status</TableHead><TableHead>Document</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {qualifications.map((q, i) => {
                    const status = q.verification_status || 'pending';
                    return (
                      <TableRow key={`${q.teacher_id}-${q.qualification_index}`} data-testid={`qual-row-${i}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                              {q.photo_url ? <img src={`${API_URL}${q.photo_url}`} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">{q.teacher_name?.split(' ').map(n=>n[0]).join('')}</span>}
                            </div>
                            <div><p className="text-sm font-medium">{q.teacher_name}</p><p className="text-[10px] text-slate-400 font-mono">{q.employee_id}</p></div>
                          </div>
                        </TableCell>
                        <TableCell><span className="text-sm font-medium">{q.degree}</span>{q.field_of_study && <span className="text-xs text-slate-500"> in {q.field_of_study}</span>}</TableCell>
                        <TableCell className="text-sm">{q.institution || '-'}</TableCell>
                        <TableCell className="text-sm">{q.year || '-'}</TableCell>
                        <TableCell>
                          <Badge className={status === 'verified' ? 'bg-green-100 text-green-700' : status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} data-testid={`qual-status-${i}`}>
                            {status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {q.document_url ? <a href={`${API_URL}${q.document_url}`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">View Doc</a> : <span className="text-[10px] text-slate-400">No doc</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {status !== 'verified' && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={() => verifyMutation.mutate({ teacher_id: q.teacher_id, qualification_index: q.qualification_index, status: 'verified', notes: '', document_url: q.document_url || '' })} data-testid={`verify-btn-${i}`}>
                                <Check className="h-3.5 w-3.5 mr-0.5" /> Verify
                              </Button>
                            )}
                            {status !== 'rejected' && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={() => verifyMutation.mutate({ teacher_id: q.teacher_id, qualification_index: q.qualification_index, status: 'rejected', notes: '', document_url: q.document_url || '' })} data-testid={`reject-btn-${i}`}>
                                <X className="h-3.5 w-3.5 mr-0.5" /> Reject
                              </Button>
                            )}
                            {status !== 'pending' && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600" onClick={() => verifyMutation.mutate({ teacher_id: q.teacher_id, qualification_index: q.qualification_index, status: 'pending', notes: '', document_url: q.document_url || '' })}>
                                Reset
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Teacher Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="teacher-form-desc">
          <DialogHeader><DialogTitle>{editingTeacher ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle><p id="teacher-form-desc" className="sr-only">Teacher form</p></DialogHeader>
          <Tabs defaultValue="basic" className="mt-2">
            <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 w-full md:inline-flex md:grid-cols-none md:w-auto">
              <TabsTrigger value="basic" className="text-xs">Basic Info</TabsTrigger>
              <TabsTrigger value="qualifications" className="text-xs">Qualifications</TabsTrigger>
              <TabsTrigger value="professional" className="text-xs">Professional</TabsTrigger>
              <TabsTrigger value="emergency" className="text-xs">Emergency</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-3 mt-3">
              <PhotoUpload url={form.photo_url} onUpload={url => setForm({ ...form, photo_url: url })} tenantId={tenantId} />
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="teacher-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="teacher-last-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="teacher-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Secondary Email</Label><Input type="email" value={form.secondary_email} onChange={e => setForm({ ...form, secondary_email: e.target.value })} /></div>
                <div><Label>Date Joined</Label><Input type="date" value={form.date_joined} onChange={e => setForm({ ...form, date_joined: e.target.value })} /></div>
                {tCampuses.length > 0 && (
                  <div><Label>Campus</Label>
                    <Select value={form.campus_id || "shared"} onValueChange={v => setForm({ ...form, campus_id: v === "shared" ? "" : v })}>
                      <SelectTrigger data-testid="teacher-campus-select"><SelectValue placeholder="Select campus" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shared">Tenant-wide (shared)</SelectItem>
                        {tCampuses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div><Label>Bio / About</Label><Textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Brief bio..." /></div>
            </TabsContent>

            <TabsContent value="qualifications" className="space-y-3 mt-3">
              {form.qualifications.length > 0 && (
                <div className="space-y-2">
                  {form.qualifications.map((q, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
                      <GraduationCap className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium">{q.degree}{q.field_of_study ? ` in ${q.field_of_study}` : ''}</p>
                        <p className="text-xs text-slate-500">{q.institution}{q.year ? ` (${q.year})` : ''}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeQualification(i)}><X className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-slate-600">Add Qualification</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Degree *</Label><Input value={newQual.degree} onChange={e => setNewQual({ ...newQual, degree: e.target.value })} placeholder="e.g. BSc, MEd, PhD" className="h-8 text-sm" data-testid="qual-degree" /></div>
                  <div><Label className="text-xs">Field of Study</Label><Input value={newQual.field_of_study} onChange={e => setNewQual({ ...newQual, field_of_study: e.target.value })} placeholder="e.g. Mathematics" className="h-8 text-sm" /></div>
                  <div><Label className="text-xs">Institution</Label><Input value={newQual.institution} onChange={e => setNewQual({ ...newQual, institution: e.target.value })} placeholder="University name" className="h-8 text-sm" /></div>
                  <div><Label className="text-xs">Year</Label><Input value={newQual.year} onChange={e => setNewQual({ ...newQual, year: e.target.value })} placeholder="2020" className="h-8 text-sm" /></div>
                </div>
                <Button size="sm" variant="outline" onClick={addQualification} disabled={!newQual.degree} data-testid="add-qual-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="professional" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee ID</Label><Input value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} placeholder="Auto-generated if empty" /></div>
                <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></div>
                <div><Label>Specialization</Label><Input value={form.specialization} onChange={e => setForm({ ...form, specialization: e.target.value })} placeholder="e.g. STEM Education" /></div>
                <div><Label>Years of Experience</Label><Input type="number" value={form.years_of_experience} onChange={e => setForm({ ...form, years_of_experience: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Employment Type</Label>
                  <Select value={form.employment_type} onValueChange={v => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full-time">Full-Time</SelectItem><SelectItem value="part-time">Part-Time</SelectItem><SelectItem value="contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Languages Spoken</Label><Input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} placeholder="English, Shona, Ndebele" /></div>
                <div><Label>Teaching License #</Label><Input value={form.teaching_license} onChange={e => setForm({ ...form, teaching_license: e.target.value })} /></div>
                <div>
                  <Label>National ID</Label>
                  <Input value={form.national_id} onChange={e => setForm({ ...form, national_id: e.target.value })} />
                  <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1"><Shield className="h-3 w-3" /> Encrypted at rest</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="emergency" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Emergency Contact Name</Label><Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
                <div><Label>Relationship</Label>
                  <Select value={form.emergency_contact_relationship || "unset"} onValueChange={v => setForm({ ...form, emergency_contact_relationship: v === "unset" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">-</SelectItem><SelectItem value="spouse">Spouse</SelectItem><SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="sibling">Sibling</SelectItem><SelectItem value="friend">Friend</SelectItem><SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isPending || !form.first_name || !form.email} data-testid="teacher-submit-btn">
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editingTeacher ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Teacher Detail Dialog */}
      <Dialog open={!!viewTeacher} onOpenChange={() => setViewTeacher(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="teacher-detail-desc">
          <DialogHeader><DialogTitle>Teacher Profile</DialogTitle><p id="teacher-detail-desc" className="sr-only">View teacher information</p></DialogHeader>
          {viewTeacher && (
            <Tabs defaultValue="profile">
              <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
                <TabsTrigger value="emergency">Emergency</TabsTrigger>
              </TabsList>
              <TabsContent value="profile" className="mt-3">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-20 h-20 rounded-full bg-slate-100 border flex items-center justify-center overflow-hidden shrink-0">
                    {viewTeacher.photo_url ? <img src={`${API_URL}${viewTeacher.photo_url}`} alt="" className="w-full h-full object-cover" /> : <UserCog className="h-8 w-8 text-slate-300" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{viewTeacher.first_name} {viewTeacher.last_name}</h3>
                    <p className="text-sm text-slate-500 font-mono">{viewTeacher.employee_id}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge className="bg-blue-100 text-blue-700 capitalize text-[10px]">{viewTeacher.employment_type || 'full-time'}</Badge>
                      {viewTeacher.department && <Badge variant="outline" className="text-[10px]">{viewTeacher.department}</Badge>}
                    </div>
                  </div>
                </div>
                {viewTeacher.bio && <p className="text-sm text-slate-600 italic border-l-2 border-slate-200 pl-3 mb-3">{viewTeacher.bio}</p>}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Email:</span><p>{viewTeacher.email}</p></div>
                  <div><span className="text-slate-500">Phone:</span><p>{viewTeacher.phone || '-'}</p></div>
                  {viewTeacher.secondary_email && <div><span className="text-slate-500">Alt Email:</span><p>{viewTeacher.secondary_email}</p></div>}
                  <div><span className="text-slate-500">Specialization:</span><p>{viewTeacher.specialization || '-'}</p></div>
                  <div><span className="text-slate-500">Experience:</span><p>{viewTeacher.years_of_experience ? `${viewTeacher.years_of_experience} years` : '-'}</p></div>
                  <div><span className="text-slate-500">Date Joined:</span><p>{viewTeacher.date_joined || '-'}</p></div>
                  <div><span className="text-slate-500">Languages:</span><p>{viewTeacher.languages || '-'}</p></div>
                  <div><span className="text-slate-500">License:</span><p>{viewTeacher.teaching_license || '-'}</p></div>
                </div>
              </TabsContent>
              <TabsContent value="qualifications" className="mt-3">
                {viewTeacher.qualifications?.length > 0 ? (
                  <div className="space-y-3">
                    {viewTeacher.qualifications.map((q, i) => {
                      const vs = q.verification_status || 'pending';
                      return (
                        <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <GraduationCap className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{q.degree}{q.field_of_study ? ` in ${q.field_of_study}` : ''}</p>
                              <Badge className={vs === 'verified' ? 'bg-green-100 text-green-700 text-[10px]' : vs === 'rejected' ? 'bg-red-100 text-red-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>{vs}</Badge>
                            </div>
                            <p className="text-xs text-slate-500">{q.institution}{q.year ? ` - ${q.year}` : ''}</p>
                            {q.document_url && <a href={`${API_URL}${q.document_url}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-0.5 inline-block">View Document</a>}
                            {q.verification_notes && <p className="text-xs text-slate-400 mt-0.5 italic">Note: {q.verification_notes}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-slate-400 text-center py-8">No qualifications recorded</p>}
              </TabsContent>
              <TabsContent value="emergency" className="mt-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Contact Name:</span><p className="font-medium">{viewTeacher.emergency_contact_name || '-'}</p></div>
                  <div><span className="text-slate-500">Phone:</span><p>{viewTeacher.emergency_contact_phone || '-'}</p></div>
                  <div><span className="text-slate-500">Relationship:</span><p className="capitalize">{viewTeacher.emergency_contact_relationship || '-'}</p></div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <Table>
          <TableHeader><TableRow><TableHead></TableHead><TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Department</TableHead><TableHead>Type</TableHead><TableHead>Experience</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {teachers.length === 0 ? <ListStateRow colSpan={8} noun="teachers" testId="teachers-state" isLoading={isLoading} error={error} count={teachers.length} onRetry={refetch} /> :
            teachers.map(t => (
              <TableRow key={t.id} data-testid={`teacher-row-${t.id}`}>
                <TableCell>
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                    {t.photo_url ? <img src={`${API_URL}${t.photo_url}`} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-medium text-slate-400">{(t.first_name?.[0] || '')}{(t.last_name?.[0] || '')}</span>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{t.employee_id}</TableCell>
                <TableCell className="font-medium">
                  {t.first_name} {t.last_name}
                  {t.campus_id && tCampuses.length > 0 && (
                    <Badge variant="outline" className="ml-2 text-[10px]" data-testid={`teacher-campus-${t.id}`}>
                      {tCampuses.find(c => c.id === t.campus_id)?.name || 'Campus'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">{t.email}</TableCell>
                <TableCell>{t.department || '-'}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] capitalize">{t.employment_type || 'full-time'}</Badge></TableCell>
                <TableCell className="text-sm">{t.years_of_experience ? `${t.years_of_experience}y` : '-'}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setViewTeacher(t)} data-testid={`view-teacher-${t.id}`}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-teacher-${t.id}`}><Settings className="h-4 w-4" /></Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { if (window.confirm(`Reset password for ${t.first_name} ${t.last_name}?`)) resetTeacherPasswordMutation.mutate(t.id); }}>
                        <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Teacher Password Reset Result Dialog */}
      <Dialog open={!!resetTeacherResult} onOpenChange={(open) => !open && setResetTeacherResult(null)}>
        <DialogContent aria-describedby="teacher-reset-desc">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-600" /> Password Reset</DialogTitle><p id="teacher-reset-desc" className="sr-only">Reset result</p></DialogHeader>
          {resetTeacherResult && (
            <div className="space-y-3">
              <p className="text-sm">Password for <strong>{resetTeacherResult.user_type}</strong> has been reset.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-blue-600 mb-1">Temporary Password</p>
                <p className="text-2xl font-mono font-bold text-blue-800 tracking-wider" data-testid="teacher-temp-password">{resetTeacherResult.temp_password}</p>
              </div>
              {resetTeacherResult.email_sent ? (
                <p className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Email sent to {resetTeacherResult.email}</p>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Email not sent. Please share the password manually.</p>
              )}
              <Button className="w-full" onClick={() => { navigator.clipboard.writeText(resetTeacherResult.temp_password); toast.success('Copied!'); }} data-testid="copy-teacher-temp-pw">Copy Password</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== CLASSES ==============
export const SchoolClasses = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const { yearId, termId, term, semesters } = useAcademicContext(tenantId);
  const [form, setForm] = useState({ name: '', grade_level: '', section: '', capacity: 40, class_teacher_id: '', campus_id: '', term_id: '' });

  const { data: classes = [], isLoading, error, refetch } = useQuery({
    queryKey: ['school-classes', tenantId, yearId, termId],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (yearId) qs.set('academic_year_id', yearId);
      if (termId) qs.set('term_id', termId);
      return api(`/tenants/${tenantId}/classes${qs.toString() ? `?${qs}` : ''}`);
    },
    enabled: !!tenantId,
  });

  const { data: cCampusesData } = useQuery({
    queryKey: ['school-campuses', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/campuses`),
    enabled: !!tenantId,
  });
  const cCampuses = cCampusesData?.items || [];

  const { data: teachers = [] } = useQuery({
    queryKey: ['school-teachers', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/teachers`),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/classes`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Class created'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-classes'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/classes/${d.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Class updated'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-classes'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/classes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Class deleted'); queryClient.invalidateQueries({ queryKey: ['school-classes'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const closeForm = () => { setShowForm(false); setEditingClass(null); setForm({ name: '', grade_level: '', section: '', capacity: 40, class_teacher_id: '', campus_id: '', term_id: '' }); };

  const openEdit = (c) => {
    setEditingClass(c);
    setForm({ name: c.name, grade_level: c.grade_level || '', section: c.section || '', capacity: c.capacity || 40, class_teacher_id: c.class_teacher?.id || '', campus_id: c.campus_id || '', term_id: c.term_id || '' });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (editingClass) {
      updateMutation.mutate({ ...form, id: editingClass.id, academic_year_id: editingClass.academic_year_id || yearId || null });
    } else {
      createMutation.mutate({ ...form, academic_year_id: yearId || null });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4" data-testid="school-classes">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Classes ({classes.length})</h2>
          {semesters.length > 0 && (
            <p className="text-xs text-slate-500" data-testid="classes-semester-scope">
              {term ? `${term.name} — semester classes plus year-long ones` : 'All classes in this academic year'}
            </p>
          )}
        </div>
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); else setShowForm(true); }}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-class-btn"><Plus className="h-4 w-4 mr-1" /> Add Class</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingClass ? 'Edit Class' : 'Add Class'}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Class Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Grade 1A" data-testid="class-name-input" /></div>
              <div><Label>Grade Level</Label><Input value={form.grade_level} onChange={e => setForm({ ...form, grade_level: e.target.value })} placeholder="e.g. 1" /></div>
              <div><Label>Section</Label><Input value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} placeholder="e.g. A" /></div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) || 40 })} /></div>
              <div>
                <Label>Class Teacher</Label>
                <Select value={form.class_teacher_id || "none"} onValueChange={v => setForm({ ...form, class_teacher_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Assign teacher" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {semesters.length > 0 && (
                <div className="col-span-2">
                  <Label>Runs in</Label>
                  <Select value={form.term_id || '__year__'} onValueChange={v => setForm({ ...form, term_id: v === '__year__' ? '' : v })}>
                    <SelectTrigger data-testid="class-semester-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__year__">Whole academic year (all semesters)</SelectItem>
                      {semesters.map(sm => <SelectItem key={sm.id} value={sm.id}>{sm.name} only</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {cCampuses.length > 0 && (
                <div className="col-span-2"><Label>Campus</Label>
                  <Select value={form.campus_id || "shared"} onValueChange={v => setForm({ ...form, campus_id: v === "shared" ? "" : v })}>
                    <SelectTrigger data-testid="class-campus-select"><SelectValue placeholder="Select campus" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shared">Tenant-wide (shared)</SelectItem>
                      {cCampuses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={isPending || !form.name} data-testid="class-submit-btn">
                {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editingClass ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {classes.length === 0 ? <div className="col-span-3"><ListState noun="classes" testId="classes-state" isLoading={isLoading} error={error} count={classes.length} onRetry={refetch} /></div> :
        classes.map(c => (
          <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`class-card-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-900">{c.name}</h3>
                <div className="flex items-center gap-1">
                  <Badge variant="outline">{c.student_count || 0} / {c.capacity}</Badge>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-class-${c.id}`}><Settings className="h-3.5 w-3.5" /></Button>
                  {(c.student_count || 0) === 0 && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => { if (confirm('Delete this class?')) deleteMutation.mutate(c.id); }} data-testid={`delete-class-${c.id}`}><X className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>
              {c.grade_level && <p className="text-xs text-slate-500">Grade {c.grade_level}{c.section ? `, Section ${c.section}` : ''}</p>}
              {c.campus_id && cCampuses.length > 0 && (
                <p className="text-xs text-slate-500 mt-1" data-testid={`class-campus-${c.id}`}>
                  Campus: <span className="font-medium">{cCampuses.find(cc => cc.id === c.campus_id)?.name || 'Shared'}</span>
                </p>
              )}
              {c.class_teacher ? (
                <p className="text-xs text-indigo-600 mt-1">Teacher: {c.class_teacher.first_name} {c.class_teacher.last_name}</p>
              ) : (
                <p className="text-xs text-amber-500 mt-1">No teacher assigned</p>
              )}
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${Math.min((c.student_count || 0) / c.capacity * 100, 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};


// ============== SUBJECTS ==============
export const SchoolSubjects = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', department: '', description: '', is_elective: false });

  const { data: subjects = [], isLoading, error, refetch } = useQuery({
    queryKey: ['school-subjects', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/subjects`),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/subjects`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Subject created'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-subjects'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/subjects/${d.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Subject updated'); closeForm(); queryClient.invalidateQueries({ queryKey: ['school-subjects'] }); },
    onError: (e) => toast.error(e.message),
  });

  const closeForm = () => { setShowForm(false); setEditingSubject(null); setForm({ name: '', code: '', department: '', description: '', is_elective: false }); };

  const openEdit = (s) => {
    setEditingSubject(s);
    setForm({ name: s.name, code: s.code, department: s.department || '', description: s.description || '', is_elective: s.is_elective || false });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (editingSubject) {
      updateMutation.mutate({ ...form, id: editingSubject.id });
    } else {
      createMutation.mutate(form);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4" data-testid="school-subjects">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Subjects ({subjects.length})</h2>
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); else setShowForm(true); }}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-subject-btn"><Plus className="h-4 w-4 mr-1" /> Add Subject</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><Label>Subject Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mathematics" data-testid="subject-name-input" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="MATH" data-testid="subject-code-input" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="Sciences" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={form.is_elective} onCheckedChange={v => setForm({ ...form, is_elective: v })} /><Label>Elective</Label></div>
              <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={isPending || !form.name || !form.code} data-testid="subject-submit-btn">
                {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editingSubject ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {subjects.length === 0 ? <ListStateRow colSpan={5} noun="subjects" testId="subjects-state" isLoading={isLoading} error={error} count={subjects.length} onRetry={refetch} /> :
            subjects.map(s => (
              <TableRow key={s.id} data-testid={`subject-row-${s.id}`}>
                <TableCell className="font-mono font-medium">{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell className="text-sm text-slate-500">{s.department || '-'}</TableCell>
                <TableCell><Badge variant={s.is_elective ? 'secondary' : 'outline'}>{s.is_elective ? 'Elective' : 'Core'}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-subject-${s.id}`}><Settings className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};


// ============== GUARDIANS ==============
export const SchoolGuardians = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm, setLinkForm] = useState({ student_id: '', guardian_id: '', relationship: 'parent' });
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', relationship: 'parent', address: '' });

  const { data: guardians = [], isLoading, error, refetch } = useQuery({
    queryKey: ['school-guardians', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/guardians`),
    enabled: !!tenantId,
  });

  const { data: studentsData } = useQuery({
    queryKey: ['school-students-for-link', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=200`),
    enabled: !!tenantId && showLinkForm,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/guardians`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Guardian added'); setShowForm(false); setForm({ first_name: '', last_name: '', phone: '', email: '', relationship: 'parent', address: '' }); queryClient.invalidateQueries({ queryKey: ['school-guardians'] }); queryClient.invalidateQueries({ queryKey: ['school-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const linkMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/guardian-links`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Guardian linked to student'); setShowLinkForm(false); setLinkForm({ student_id: '', guardian_id: '', relationship: 'parent' }); queryClient.invalidateQueries({ queryKey: ['school-guardians'] }); queryClient.invalidateQueries({ queryKey: ['school-students'] }); },
    onError: (e) => toast.error(e.message),
  });

  // Edit / delete
  const [editingGuardian, setEditingGuardian] = useState(null);
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api(`/tenants/${tenantId}/guardians/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => { toast.success('Guardian updated'); setEditingGuardian(null); queryClient.invalidateQueries({ queryKey: ['school-guardians'] }); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/guardians/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Guardian removed'); queryClient.invalidateQueries({ queryKey: ['school-guardians'] }); queryClient.invalidateQueries({ queryKey: ['school-students'] }); },
    onError: (e) => toast.error(e.message),
  });

  const students = studentsData?.students || [];

  return (
    <div className="space-y-4" data-testid="school-guardians">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Guardians ({guardians.length})</h2>
        <div className="flex gap-2">
          <Dialog open={showLinkForm} onOpenChange={setShowLinkForm}>
            <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="link-guardian-btn"><ChevronRight className="h-4 w-4 mr-1" /> Link to Student</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Link Guardian to Student</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label>Guardian *</Label>
                  <Select value={linkForm.guardian_id} onValueChange={v => setLinkForm({ ...linkForm, guardian_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select guardian" /></SelectTrigger>
                    <SelectContent>{guardians.map(g => <SelectItem key={g.id} value={g.id}>{[g.first_name, g.last_name].filter(Boolean).join(' ') || g.full_name || g.name || '—'}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Student *</Label>
                  <Select value={linkForm.student_id} onValueChange={v => setLinkForm({ ...linkForm, student_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Select value={linkForm.relationship} onValueChange={v => setLinkForm({ ...linkForm, relationship: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="parent">Parent</SelectItem><SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sponsor">Sponsor</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowLinkForm(false)}>Cancel</Button>
                <Button onClick={() => linkMutation.mutate(linkForm)} disabled={linkMutation.isPending || !linkForm.student_id || !linkForm.guardian_id} data-testid="link-guardian-submit">
                  {linkMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-guardian-btn"><Plus className="h-4 w-4 mr-1" /> Add Guardian</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Guardian</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="guardian-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="guardian-last-name" /></div>
                <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+263..." data-testid="guardian-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div>
                  <Label>Relationship</Label>
                  <Select value={form.relationship} onValueChange={v => setForm({ ...form, relationship: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="parent">Parent</SelectItem><SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sponsor">Sponsor</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.first_name || !form.phone} data-testid="guardian-submit-btn">
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Add
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Relationship</TableHead><TableHead>Children</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {guardians.length === 0 ? <ListStateRow colSpan={6} noun="guardians" testId="guardians-state" isLoading={isLoading} error={error} count={guardians.length} onRetry={refetch} /> :
            guardians.map(g => (
              <TableRow key={g.id} data-testid={`guardian-row-${g.id}`}>
                <TableCell className="font-medium">{[g.first_name, g.last_name].filter(Boolean).join(' ') || g.full_name || g.name || '—'}</TableCell>
                <TableCell>{g.phone}</TableCell>
                <TableCell className="text-sm">{g.email || '-'}</TableCell>
                <TableCell className="capitalize">{g.relationship}</TableCell>
                <TableCell>{g.children?.map(c => c.name).join(', ') || '-'}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit guardian"
                    data-testid={`guardian-edit-${g.id}`}
                    onClick={() => setEditingGuardian({ ...g })}>
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" title="Remove guardian"
                    data-testid={`guardian-delete-${g.id}`}
                    onClick={() => {
                      const linked = (g.children || []).length;
                      const confirmMsg = linked
                        ? `Remove ${g.first_name} ${g.last_name}? This guardian is linked to ${linked} student${linked > 1 ? 's' : ''}; the links will be removed but students stay.`
                        : `Remove ${g.first_name} ${g.last_name}?`;
                      if (window.confirm(confirmMsg)) {
                        deleteMutation.mutate(g.id);
                      }
                    }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Guardian Dialog */}
      <Dialog open={!!editingGuardian} onOpenChange={(o) => !o && setEditingGuardian(null)}>
        <DialogContent data-testid="guardian-edit-dialog">
          <DialogHeader><DialogTitle>Edit Guardian</DialogTitle></DialogHeader>
          {editingGuardian && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><Label>First Name *</Label><Input value={editingGuardian.first_name || ''} onChange={e => setEditingGuardian({ ...editingGuardian, first_name: e.target.value })} /></div>
              <div><Label>Last Name *</Label><Input value={editingGuardian.last_name || ''} onChange={e => setEditingGuardian({ ...editingGuardian, last_name: e.target.value })} /></div>
              <div><Label>Phone *</Label><Input value={editingGuardian.phone || ''} onChange={e => setEditingGuardian({ ...editingGuardian, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={editingGuardian.email || ''} onChange={e => setEditingGuardian({ ...editingGuardian, email: e.target.value })} /></div>
              <div>
                <Label>Relationship</Label>
                <Select value={editingGuardian.relationship || 'parent'} onValueChange={v => setEditingGuardian({ ...editingGuardian, relationship: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="parent">Parent</SelectItem><SelectItem value="guardian">Guardian</SelectItem><SelectItem value="sponsor">Sponsor</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Occupation</Label><Input value={editingGuardian.occupation || ''} onChange={e => setEditingGuardian({ ...editingGuardian, occupation: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={editingGuardian.address || ''} onChange={e => setEditingGuardian({ ...editingGuardian, address: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditingGuardian(null)}>Cancel</Button>
            <Button
              data-testid="guardian-edit-save"
              disabled={updateMutation.isPending || !editingGuardian?.first_name || !editingGuardian?.phone}
              onClick={() => {
                const { id, children, _id, tenant_id, created_at, updated_at, ...payload } = editingGuardian;
                updateMutation.mutate({ id: editingGuardian.id, payload });
              }}
            >
              {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
